/**
 * Quadratisches Artwork für Alben, EPs und Singles.
 *
 * Ersetzt in diesem Bereich das Notizblock-Bild (ui/notepad.ts): eine
 * Veröffentlichung ist eine Sammlung von Musik-Releases, kein Heft. Die
 * Fläche zeigt entweder das eingefügte Cover, sauber quadratisch – oder,
 * ohne Bild, eine weiße Schallplattenhülle statt eines grauen
 * UI-Platzhalters: minimalistisch, mit dezentem Schatten und einer ganz
 * leisen Andeutung der Plattenhülle darunter, kein Retro-Vinyl-Kitsch.
 *
 * Bewusst ein eigenes, kleines Modul statt eine Notizblock-Variante: die
 * beiden Metaphern (Buch/Block vs. Cover/Hülle) sollen sich auch im Code
 * nicht vermischen.
 */
import { readDataUrl } from '../features/canvas/audio.ts';
import { h } from './dom.ts';

export interface ArtworkOptions {
  /** Cover-Bild als Data-URL. Fehlt es, erscheint die weiße Hülle. */
  image?: string;
  /** Zeichen auf der leeren Hülle, z. B. das Emblem der Art (Album/EP/Single). */
  emblem?: string;
  /** Kleine Marke oben rechts, z. B. die Anzahl der Songs. */
  badge?: string;
  /** Für Screenreader; sichtbarer Titel steht daneben/darunter im Aufrufer. */
  label: string;
}

/** Baut die quadratische Cover-Fläche einer Veröffentlichung. */
export function renderArtwork(opts: ArtworkOptions): HTMLElement {
  const box = h('div', {
    class: 'artwork' + (opts.image ? '' : ' artwork--empty'),
    attrs: { role: 'img', 'aria-label': opts.label },
  });

  if (opts.image) {
    const img = h('img', { class: 'artwork__image', attrs: { alt: '' } });
    img.src = opts.image;
    box.append(img);
  } else {
    box.append(
      h(
        'div',
        { class: 'artwork__sleeve' },
        h('div', { class: 'artwork__ring' }),
        h('span', { class: 'artwork__emblem', text: opts.emblem ?? '◉' }),
      ),
    );
  }

  if (opts.badge) {
    box.append(h('span', { class: 'artwork__badge', text: opts.badge }));
  }

  return box;
}

/** Liest eine Bilddatei als Data-URL, fürs Ablegen als Artwork. */
export function readImage(file: File): Promise<string> {
  return readDataUrl(file);
}

/** Öffnet die native Bildauswahl und liefert die gewählte Datei. */
export function pickImageFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';
    input.addEventListener('change', () => {
      const file = input.files?.[0] ?? null;
      input.remove();
      resolve(file);
    });
    document.body.append(input);
    input.click();
  });
}
